import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { loginTotp } from '@/lib/api/twofa';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

const totpSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.'),
});
const backupSchema = z.object({
  code: z.string().trim().min(8, 'Enter one of your saved backup codes.'),
});

type FormValues = { code: string };

/**
 * Real TOTP login challenge (AF-003b). Reached only after `/auth/admin/login`
 * returns `requires_2fa` + a `challenge_token` (held in the auth store, never
 * persisted). The code is exchanged at `/auth/2fa/login` for the real token
 * pair; only then is a session established.
 */
export function TwoFactorPage() {
  const navigate = useNavigate();
  const challengeToken = useAuthStore((s) => s.challengeToken);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const markTotpChallenged = useAuthStore((s) => s.markTotpChallenged);
  const [submitting, setSubmitting] = useState(false);
  const [useBackup, setUseBackup] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(useBackup ? backupSchema : totpSchema),
    defaultValues: { code: '' },
  });

  // No live challenge (direct hit / refresh cleared in-memory state) →
  // send the operator back to sign in rather than stranding them here.
  if (!challengeToken) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(values: FormValues) {
    if (!challengeToken) return;
    setSubmitting(true);
    try {
      const tokens = await loginTotp(challengeToken, values.code.trim());
      setTokens(tokens.access_token, tokens.refresh_token);
      setUser({
        id: tokens.user.id,
        phone: tokens.user.phone,
        email: tokens.user.email,
        role: tokens.user.role,
      });
      markTotpChallenged();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        form.setError('code', {
          message: useBackup ? 'That backup code is invalid or already used.' : 'Invalid code.',
        });
      } else if (status === 429) {
        toast.error('Too many attempts — try again in a bit.');
      } else {
        toast.error('Could not verify code', { description: 'Backend may be offline.' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  function toggleBackup() {
    const next = !useBackup;
    setUseBackup(next);
    form.reset({ code: '' });
    form.clearErrors();
  }

  return (
    <Card>
      <CardHeader className="space-y-2 text-center">
        <ShieldCheck className="text-primary mx-auto h-10 w-10" />
        <CardTitle className="text-2xl">Two-factor authentication</CardTitle>
        <CardDescription>
          {useBackup
            ? 'Enter one of the backup codes you saved when enabling 2FA.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{useBackup ? 'Backup code' : 'Authenticator code'}</FormLabel>
                  <FormControl>
                    <Input
                      inputMode={useBackup ? 'text' : 'numeric'}
                      autoComplete="one-time-code"
                      maxLength={useBackup ? 32 : 6}
                      placeholder={useBackup ? 'xxxx-xxxx' : '123456'}
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Verify
            </Button>
          </form>
        </Form>
        <Button type="button" variant="link" className="w-full" onClick={toggleBackup}>
          {useBackup ? 'Use your authenticator app instead' : 'Use a backup code'}
        </Button>
      </CardContent>
    </Card>
  );
}
